import { useMemo } from "react";
import { useUserContext } from "../../../../../contexts/UserContext";
import {
  JsonSchemaPropertyMap,
  RowData,
  RowDataObject,
  RowDataReducerAction,
  RowDataReducerState,
  RowDataValueType,
  SaveRecordRequest,
} from "../../../../../types";
import {
  exportCorelationRecordsToExcel,
  validateBulkUploadRecords,
} from "../../../helpers";
import {
  parseCorelationExcelFile,
  getAllFieldProperties,
} from "../../../helpers/importRjeReference";
import { useFileUpload } from "../../table-actions/FileUpload/useFileUpload";
import FileUploadDialog from "../../table-actions/FileUpload/FileUploadDialog";
import { DEFAULT_TOAST } from "../../../constants";
import { ValueGetterParams } from "ag-grid-community";

interface CorrelationFileUploadProps {
  isOpen: boolean;
  onClose: () => void;
  rowDataReducerState: RowDataReducerState;
  propertyKey: string;
  corelationFieldProperties: JsonSchemaPropertyMap;
  title: string;
  getFieldDisplayValue: (
    fieldName: string,
    fieldValue: RowDataValueType,
  ) => RowDataValueType;
  dispatch: React.Dispatch<RowDataReducerAction>;
}

export default function CorrelationFileUpload({
  isOpen,
  onClose,
  rowDataReducerState,
  propertyKey,
  corelationFieldProperties,
  title,
  dispatch,
  getFieldDisplayValue,
}: Readonly<CorrelationFileUploadProps>): JSX.Element {
  const { schema, rowData } = rowDataReducerState;

  const { selected, iamUserState, setupBaseRequestInfo } = useUserContext();

  const handleParseFile = async (file: File) => {
    return parseCorelationExcelFile(
      file,
      schema,
      title,
      corelationFieldProperties,
      iamUserState,
    );
  };

  const handleValidateFile = async (data: RowData[]) => {
    const request: SaveRecordRequest = {
      ...setupBaseRequestInfo,
      record: data,
    };
    return validateBulkUploadRecords(request);
  };

  const {
    validatedData,
    uploadState,
    attachedFile,
    toastMessage,
    hasErrors,
    setToastMessage,
    resetUpload,
    removeFile,
    handleFileChange,
    handleValidate,
  } = useFileUpload({
    parseFile: handleParseFile,
    validateFile: handleValidateFile,
  });

  const handleImport = (): void => {
    const updatedRowData = {
      ...rowData,
      [propertyKey]: validatedData,
    } as RowData;

    dispatch({
      type: "UPDATE_ROW_DATA_REDUCER_STATE",
      payload: {
        rowData: updatedRowData,
      },
    });
    onClose();
  };

  const columnDefs = useMemo(
    () =>
      Object.entries(getAllFieldProperties(schema, corelationFieldProperties))
        .filter(([, property]) => !property?.isHidden)
        .map(([field, property]) => ({
          field,
          headerName: property.title,
          valueGetter: ({ data }: ValueGetterParams) =>
            getFieldDisplayValue(field, data?.[field]),
        })),
    [corelationFieldProperties, schema],
  );

  const handleDownloadTemplate = async () => {
    const fileName = `${title}_File_Upload_Template`
      .replace(/\s*-\s*/g, "-")
      .replace(/\s+/g, "-");

    await exportCorelationRecordsToExcel({
      fileName,
      schema,
      rowData,
      corelationRows: [{}],
      propertyKey,
      selected,
      iamUserState,
    });
  };

  const handleDownloadErrorReport = async () => {
    const fileName = `${title}_Error_Report`
      .replace(/\s*-\s*/g, "-")
      .replace(/\s+/g, "-");
    await exportCorelationRecordsToExcel({
      fileName: fileName,
      schema,
      rowData,
      corelationRows: validatedData as RowDataObject[],
      propertyKey,
      selected,
      iamUserState,
    });
  };

  return (
    <FileUploadDialog
      isOpen={isOpen}
      title={`Upload ${title}`}
      uploadState={uploadState}
      attachedFile={attachedFile}
      toastMessage={toastMessage}
      columnDefs={columnDefs}
      tableData={validatedData}
      hasErrors={hasErrors}
      onClose={() => {
        resetUpload();
        onClose();
      }}
      onFileChange={handleFileChange}
      onFileRemove={removeFile}
      onValidate={handleValidate}
      onImport={handleImport}
      onReset={resetUpload}
      onDownloadTemplate={handleDownloadTemplate}
      onDownloadErrorReport={handleDownloadErrorReport}
      onClearToast={() => setToastMessage(DEFAULT_TOAST)}
    />
  );
}
